//src/components/VerifyOTP.js
import { useEffect, useMemo, useRef, useState } from 'react';
import { BsWhatsapp } from 'react-icons/bs';
import { HiArrowLeft } from 'react-icons/hi';
import { MdRefresh } from 'react-icons/md';
import { RiShieldCheckLine } from 'react-icons/ri';

import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../axios';
import '../styles/VerifyOTP.css';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;

const VerifyOTP = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const inputRefs = useRef([]);
    const redirectTimeoutRef = useRef(null);

    const phone = location.state?.phone || sessionStorage.getItem('mp_verify_phone') || '';
    const name = location.state?.name || sessionStorage.getItem('mp_verify_name') || '';

    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
    const [canResend, setCanResend] = useState(false);

    const normalizePhone = (value = '') => {
        let phoneValue = String(value)
            .replace(/[^\d+]/g, '')
            .trim();

        if (!phoneValue) return '';

        if (phoneValue.startsWith('+')) {
            phoneValue = phoneValue.slice(1);
        }

        if (phoneValue.startsWith('0')) {
            return `62${phoneValue.slice(1)}`;
        }

        if (phoneValue.startsWith('8')) {
            return `62${phoneValue}`;
        }

        if (phoneValue.startsWith('62')) {
            return phoneValue;
        }

        return phoneValue;
    };

    const maskedPhone = useMemo(() => {
        if (!phone) return '08xx-xxxx-xxxx';

        const normalized = normalizePhone(phone);
        if (normalized.length < 8) return phone;

        return normalized.replace(/^(\d{4})\d+(\d{4})$/, '$1****$2');
    }, [phone]);

    const allFilled = otp.every((digit) => digit !== '');

    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    useEffect(() => {
        if (countdown <= 0) {
            setCanResend(true);
            return;
        }

        const timer = window.setTimeout(() => {
            setCountdown((current) => current - 1);
        }, 1000);

        return () => window.clearTimeout(timer);
    }, [countdown]);

    useEffect(() => {
        if (!success) return;

        redirectTimeoutRef.current = window.setTimeout(() => {
            navigate('/login', {
                state: { message: 'Akun berhasil diverifikasi! Silakan login.' },
            });
        }, 1800);

        return () => {
            if (redirectTimeoutRef.current) {
                window.clearTimeout(redirectTimeoutRef.current);
            }
        };
    }, [success, navigate]);

    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;

        const digit = value.slice(-1);

        setOtp((prev) => {
            const next = [...prev];
            next[index] = digit;
            return next;
        });
        setError('');

        if (digit && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, event) => {
        if (event.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
            return;
        }

        if (event.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
            return;
        }

        if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
            return;
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();

        const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);

        if (!pasted) return;

        const nextOtp = pasted.split('');
        while (nextOtp.length < OTP_LENGTH) nextOtp.push('');

        setOtp(nextOtp);
        setError('');
        inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    };

    const handleVerify = async () => {
        const code = otp.join('');

        if (code.length !== OTP_LENGTH) {
            setError('Masukkan kode OTP 6 digit yang dikirim ke WhatsApp Anda.');
            return;
        }

        if (!phone) {
            setError('Nomor WhatsApp tidak ditemukan.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axiosInstance.post('/auth/verify-whatsapp', {
                phone_number: normalizePhone(phone),
                otp: code,
            });

            if (response.data?.success) {
                setSuccess(true);
            } else {
                setError(response.data?.message || 'Kode OTP tidak valid.');
            }
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                    err?.response?.data?.errors?.otp?.[0] ||
                    'Kode OTP tidak valid. Periksa kembali.'
            );
            setOtp(Array(OTP_LENGTH).fill(''));
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!canResend || resendLoading) return;

        if (!phone) {
            setError('Nomor WhatsApp tidak ditemukan.');
            return;
        }

        setResendLoading(true);
        setError('');

        try {
            const response = await axiosInstance.post('/auth/resend-otp', {
                phone_number: normalizePhone(phone),
            });

            if (response.data?.success) {
                setOtp(Array(OTP_LENGTH).fill(''));
                setCountdown(RESEND_COOLDOWN);
                setCanResend(false);
                inputRefs.current[0]?.focus();
            } else {
                setError(response.data?.message || 'Gagal mengirim ulang OTP.');
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'Gagal mengirim ulang OTP. Coba lagi.');
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className="verify-otp-page">
            <div className="verify-otp-bg" aria-hidden="true" />
            <div className="verify-otp-card">
                <div className="verify-otp-card__header">
                    <div className="verify-otp-card__icon">{success ? <RiShieldCheckLine /> : <BsWhatsapp />}</div>

                    <h2 className="verify-otp-card__title">
                        {success ? 'Verifikasi Berhasil!' : 'Verifikasi WhatsApp'}
                    </h2>

                    <p className="verify-otp-card__subtitle">
                        {success ? (
                            'Akun Anda telah aktif. Mengalihkan ke halaman login...'
                        ) : (
                            <>
                                Kode OTP 6 digit telah dikirim ke
                                <br />
                                <strong className="verify-otp-card__phone">{maskedPhone}</strong>
                                {name && (
                                    <span>
                                        {' '}
                                        atas nama <strong className="verify-otp-card__name">{name}</strong>
                                    </span>
                                )}
                            </>
                        )}
                    </p>
                </div>

                <div className="verify-otp-card__body">
                    {!success ? (
                        <>
                            <div className="verify-otp-card__info">
                                Masukkan kode OTP yang dikirim melalui WhatsApp untuk mengaktifkan akun Anda.
                            </div>

                            <div className="verify-otp-otp-group" onPaste={handlePaste}>
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        className={`verify-otp-otp-input ${digit ? 'filled' : ''}`}
                                        type="tel"
                                        inputMode="numeric"
                                        pattern="\d*"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        autoFocus={index === 0}
                                        disabled={loading || resendLoading}
                                        aria-label={`Digit OTP ${index + 1}`}
                                    />
                                ))}
                            </div>

                            {error && (
                                <div className="verify-otp-alert verify-otp-alert--error" role="alert">
                                    {error}
                                </div>
                            )}

                            <button
                                type="button"
                                className="verify-otp-submit"
                                onClick={handleVerify}
                                disabled={!allFilled || loading || resendLoading}
                            >
                                {loading ? (
                                    <>
                                        <span className="verify-otp-spinner" />
                                        Memverifikasi...
                                    </>
                                ) : (
                                    'Verifikasi Sekarang'
                                )}
                            </button>

                            <div className="verify-otp-resend-wrap">
                                {canResend ? (
                                    <button
                                        type="button"
                                        onClick={handleResend}
                                        disabled={resendLoading}
                                        className="verify-otp-resend-btn"
                                    >
                                        <MdRefresh size={16} />
                                        {resendLoading ? 'Mengirim...' : 'Kirim ulang kode OTP'}
                                    </button>
                                ) : (
                                    <p className="verify-otp-resend-text">
                                        Kirim ulang dalam <span className="verify-otp-resend-count">{countdown}s</span>
                                    </p>
                                )}
                            </div>

                            <div className="verify-otp-footer">
                                <button
                                    type="button"
                                    onClick={() => navigate('/register')}
                                    className="verify-otp-back-btn"
                                >
                                    <HiArrowLeft size={14} />
                                    Kembali ke Pendaftaran
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="verify-otp-success">
                            <div className="verify-otp-success__icon">
                                {' '}
                                <RiShieldCheckLine />
                            </div>
                            <p className="verify-otp-success__text">
                                Akun Anda aktif dan siap digunakan. Anda akan dialihkan otomatis...
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyOTP;
